import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import Autorenew from "@mui/icons-material/Autorenew";
import Close from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LoadingBoxes from "../../components/LoadingBoxes/LoadingBoxes";
import { fetchModelAsset, verifyResourceExists } from "../../services/modelService";
import { SERVER_BASE_URL } from "../../config";
import ContactCCRForm from "../../components/ContactCCRForm/ContactCCRForm";
import { useAuth } from "../../context/authContext";
import { useOptions } from "../../context/optionContext";
import { canShowContactCcr } from "../../utilities/contactCcrAvailability";

// Mirrors ModelViewer's own messages so the legacy Glovius model and the GLB model present
// an identical wait to the user — this page exists specifically so the two 3D formats stop
// behaving differently (GLB rendered in-app, HTML thrown into a bare browser tab).
const gloviusLoadingMessages = [
	"Loading 3D Model...",
	"Loading Products and Machine Data...",
	"Rendering Model View...",
];
// Index of the message used once the document has arrived and Glovius is parsing the CAD data.
const GLOVIUS_RENDERING_STEP = 2;
// In-house (iSTP2HTML) pages carry their model inline (20+ MB) and take the same streamed
// download as Glovius; the curtain then stays up, over the page's own loader, until the page
// reports the model on screen (with its real progress in between), so only these two
// messages can appear.
const inhouseLoadingMessages = [
	"Loading 3D Model...",
	"Loading Products and Machine Data...",
];
const INHOUSE_RENDERING_STEP = 1;

// The generated page's folder names its kind: processFile writes in-house pages under
// internal/internalhtmls/ and Glovius wrappers under ithena-htmls/. Used when a caller passed
// a URL but no kind, and by the deep-link probe below.
const inferKindFromUrl = (url = "") => {
	if (/\/3D\/(?:internal\/internalhtmls|inhouse-htmls|inhouse\/amf)\//i.test(url)) return "inhouse";
	if (/\/3D\/ithena-htmls\//i.test(url)) return "glovius";
	return "";
};

// There are TWO waits here, and the user must only ever see one loader for them:
//   1. downloading the document — 3.6-16 MB with the CAD model inlined as base64, uncompressed
//   2. Glovius parsing that payload — up to ~30s, during which it shows its OWN loader
// The iframe's load event fires between the two. Hiding the overlay there (the first version
// of this page) meant the app's loader handed over to Glovius's differently-styled one
// mid-wait. The overlay now stays up until the model is genuinely ready, which also keeps
// Glovius's loader hidden behind it.
//
// Only wait 1 is measurable, and it is measured for real: this page downloads the document
// itself with a streaming reader and hands the bytes to the iframe as a blob: URL, instead of
// letting the iframe fetch it. Wait 2 happens inside Glovius's runtime — it exposes no
// progress callback, only ControlLoadedEvent/FileLoadedEvent — so it is shown as an
// INDETERMINATE phase. The two are deliberately presented differently (a real percentage,
// then no number at all) rather than smeared into one invented number: a percentage that
// stalls at 96 for thirty seconds teaches the user the number is a lie.
const STEP_INTERVAL_MS = 4000;
// Safety valve: if the readiness ping never arrives — a model file generated before the
// server started sending it, or Glovius erroring during parse — reveal the iframe anyway
// rather than trapping the user behind a loader that can never finish.
const READY_FALLBACK_MS = 45000;
// Repaint the download counter at most once per 128 KB. The reader hands back 16-64 KB
// chunks, so a 16 MB document arrives in roughly 250-1000 of them; calling setState on every
// chunk would re-render this page a few hundred times a second to move a number the user can
// only perceive changing a few dozen times.
const PROGRESS_PAINT_BYTES = 131072;
// The download owns 0-90% of the bar, the Glovius parse owns 90-99%. Deliberately uneven and
// identical to the GLB viewer's split (ModelViewer.js): only the download is measurable, so
// the unmeasurable phase gets a small, visibly slower slice instead of being smeared across
// the whole bar. 100 belongs to the real readiness ping alone.
const DOWNLOAD_SHARE = 90;
const PARSE_CEILING = 99;
const PARSE_TICK_MS = 200;
// ponytail: seat-of-the-pants estimate, same knob as ModelViewer.js's PARSE_MS_PER_MB (125 for
// Draco-compressed GLB). 1800 is back-solved from the only datum this file has ever recorded —
// the header comment above, which says a 3.6-16 MB document takes "up to ~30s" to parse. At
// 1800 ms/MB a 16 MB document expects 28.8s, which is that sentence turned into arithmetic.
// TUNING KNOB: if the bar reaches ~99 and visibly waits for the ping, raise it; if the ping
// keeps snapping it to 100 from ~92, lower it. Replace with a measured median given telemetry.
const PARSE_MS_PER_MB = 1800;
// The document is always text/html written as UTF-8 (model.controller.js writes it with
// fs.writeFile(..., "utf8")). The Blob's type is pinned rather than copied from the response
// because /models is served by three different handlers — express.static, an S3 passthrough
// that echoes whatever ContentType the object was uploaded with, and a sendFile fallback —
// and a blob typed application/octet-stream makes the iframe offer a download instead of
// rendering the viewer.
const DOCUMENT_MIME = "text/html; charset=utf-8";
const normalizeHtmlOrientation = (options = {}) => {
	const normalize = (value, fallback) => {
		const axis = String(value || fallback).trim().toUpperCase();
		return ["X", "Y", "Z"].includes(axis) ? axis : fallback;
	};
	const up = normalize(options.modelViewerOrientationUp || options.modelViewerUpAxis, "Y");
	const fallbackLeft = up === "X" ? "Z" : "X";
	const fallbackRight = ["X", "Y", "Z"].find((axis) => axis !== up && axis !== fallbackLeft) || "Y";
	const left = normalize(options.modelViewerOrientationLeft, fallbackLeft);
	const right = normalize(options.modelViewerOrientationRight, fallbackRight);
	const corner = String(options.modelViewerDefaultCameraCorner || "isometric").toLowerCase();
	const cameraCorner = ["isometric", "front", "back", "left", "right", "top", "bottom"].includes(corner) ? corner : "isometric";
	if (new Set([up, left, right]).size === 3) return { up, left, right, cameraCorner };
	return { up, left: fallbackLeft, right: fallbackRight, cameraCorner };
};

/**
 * Formats a byte count for the download counter.
 *
 * Fixed to MB with one decimal, with no KB/GB ladder, because these documents are always
 * 3.6-16 MB — a unit-picking helper would be dead code for every input this page can ever
 * receive, and a counter whose UNIT changes mid-download reads as jumpy rather than precise.
 *
 * @param {number} bytes Bytes received so far.
 * @returns {string} e.g. "8.2 MB".
 */
const formatMb = (bytes) => `${(bytes / 1048576).toFixed(1)} MB`;

// Top-right refresh/close buttons, kept identical to the GLB viewer's pair.
//
// Each carries its own surface: a white rounded tile with a hairline border and a soft
// shadow, so a grey glyph stays findable over whatever the model happens to be showing
// behind it. The cluster Box below still has no surface of its own — the tile is on the
// buttons.
//
// ONE appearance at every width — no compact variant. An earlier version tinted these
// `primary.main` and grew them to 44px below 1025px, which made the same corner of the
// same product look like two different applications depending on screen size. These
// values spell out MUI's default medium IconButton (8px padding around a 24px glyph,
// `action.active`), which is what ModelViewer.js renders by passing no sx at all.
//
// The coarse-pointer overlay is the reason dropping to 40px is safe: 40px is under the
// 44px touch-target floor, so on touch devices a transparent ::after widens the HIT AREA
// without altering a single rendered pixel. ButtonBase is already `position: relative`,
// so `inset` resolves against the button itself.
//
// Shared by both buttons so they can never drift apart.
const controlButtonSx = {
	color: "action.active",
	width: 40,
	height: 40,
	backgroundColor: "#ffffff",
	border: "1px solid #e6e8ec",
	borderRadius: "12px",
	boxShadow: "0 2px 8px rgba(16, 22, 27, 0.10)",
	"&:hover": {
		backgroundColor: "#f6f8fa",
	},
	"@media (pointer: coarse)": {
		"&::after": {
			content: '""',
			position: "absolute",
			inset: "-2px",
		},
	},
};

/**
 * Full-page host for the legacy Glovius HTML models.
 *
 * These are whole HTML documents with their own runtime, not data React can render, so the
 * only two ways to show one are a new tab or an iframe. This is the iframe: the app keeps its
 * route and its loading treatment, matching /model-viewer. Chrome inside the frame (title,
 * toolbar, close) is Glovius's own, skinned to match by the glb-parity block in
 * model.controller.js — so nothing here duplicates it.
 *
 * The generated URL and its kind normally arrive via router state from the caller
 * (MultipleModelViewer or ModelButton), which already holds every parameter the POST needs.
 * On a refresh or a pasted link that state is gone, so it probes the deterministic filenames
 * the server writes — `internal/internalhtmls/<name>.html` for an in-house page, then
 * `ithena-htmls/ithena-<name>.html` for a Glovius one — and only errors when neither exists.
 *
 * @returns {JSX.Element} The viewer page.
 */
const LegacyHtmlModelViewer = () => {
	const { fileName } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { user } = useAuth();
	const { optionKeys } = useOptions();
	const htmlOrientation = useMemo(() => normalizeHtmlOrientation(optionKeys || {}), [optionKeys]);
	const showContactCcr = canShowContactCcr(user, optionKeys);

	const stateUrl = location?.state?.modelUrl;
	const fromPath = location?.state?.fromPath;
	const machineName = location?.state?.machineName;

	const iframeRef = useRef(null);
	const [modelUrl, setModelUrl] = useState(stateUrl || "");
	// Which kind of page this is decides the whole loading treatment. The caller passes the
	// server's answer; a pasted link has no state, so the probe below settles it instead.
	const [kind, setKind] = useState(() => location?.state?.kind || inferKindFromUrl(stateUrl));
	const isInhouse = kind === "inhouse";
	const loadingMessages = isInhouse ? inhouseLoadingMessages : gloviusLoadingMessages;
	const renderingStep = isInhouse ? INHOUSE_RENDERING_STEP : GLOVIUS_RENDERING_STEP;
	const [ready, setReady] = useState(false);
	const [error, setError] = useState("");
	const [ccrOpen, setCcrOpen] = useState(false);
	const [step, setStep] = useState(0);
	// null, not 0 — LoadingBoxes renders a number only for a non-NaN number, so null is how this
	// page says "no figure I can stand behind yet" and gets the bare animation instead. Starting
	// at 0 would flash a "0%" for the first 128 KB, before any Content-Length has been judged.
	const [percentage, setPercentage] = useState(null);
	const [reloadToken, setReloadToken] = useState(0);
	// Streaming-download state. `blobUrl` doubles as the phase marker on the streamed path:
	// it is set at the exact moment the last byte lands, so a non-empty value means "download
	// finished, Glovius is parsing".
	const [blobUrl, setBlobUrl] = useState("");
	const [receivedBytes, setReceivedBytes] = useState(0);
	// 0 means "no total we are willing to stand behind" — see the fetch effect.
	const [trustedTotalBytes, setTrustedTotalBytes] = useState(0);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const resolvedRef = useRef(false);
	const fallbackTimerRef = useRef(null);

	// Deterministic locations of the generated pages, one per kind (model.controller.js
	// processFile). The in-house folder only ever holds in-house pages, so a hit there also
	// settles the kind.
	const inhouseFallbackUrl = useMemo(
		() => (fileName ? `${SERVER_BASE_URL}/models/3D/internal/internalhtmls/${fileName}.html` : ""),
		[fileName],
	);
	const gloviusFallbackUrl = useMemo(
		() => (fileName ? `${SERVER_BASE_URL}/models/3D/ithena-htmls/ithena-${fileName}.html` : ""),
		[fileName],
	);

	// Cache-busting URL for the frame; identical to modelUrl until a refresh is requested.
	// Declared up here because the download effect keys off it — changing it is what defines
	// "start over", for both the streamed path and the direct fallback.
	const framedUrl = useMemo(() => {
		if (!modelUrl || !reloadToken) return modelUrl;
		const separator = modelUrl.includes("?") ? "&" : "?";
		return `${modelUrl}${separator}_hard_refresh=${reloadToken}`;
	}, [modelUrl, reloadToken]);

	const ccrPrefill = useMemo(() => ({
		machine_number: machineName || fileName || "",
		subject: `Need help with ${machineName || fileName || "3D model"}`,
		message: `Hello CCR team, I need help with ${machineName || fileName || "this 3D model"}.`,
	}), [fileName, machineName]);

	const sendContactCcrAvailability = useCallback(() => {
		try {
			iframeRef.current?.contentWindow?.postMessage({
				source: "iecom-host",
				stage: "contact-ccr-availability",
				showContactCcr,
			}, "*");
		} catch (err) {
			console.warn("HtmlModelViewer: failed to send CCR availability to iframe.", err);
		}
	}, [showContactCcr]);

	const sendHtmlOrientation = useCallback((orientation = htmlOrientation) => {
		try {
			iframeRef.current?.contentWindow?.postMessage({
				source: "iecom-host",
				stage: "html-orientation",
				orientation,
			}, "*");
		} catch (err) {
			console.warn("HtmlModelViewer: failed to send HTML orientation.", err);
		}
	}, [htmlOrientation]);

	// Resolve the URL once. Router state wins; otherwise probe the deterministic paths,
	// in-house first (its folder is unambiguous), then the Glovius wrapper.
	useEffect(() => {
		if (resolvedRef.current) return;
		resolvedRef.current = true;
		if (stateUrl) return;

		let cancelled = false;
		(async () => {
			if (!inhouseFallbackUrl) {
				setError("No 3D model was specified.");
				return;
			}
			const inhouseExists = await verifyResourceExists(inhouseFallbackUrl);
			if (cancelled) return;
			if (inhouseExists) {
				setKind("inhouse");
				setModelUrl(inhouseFallbackUrl);
				return;
			}
			const gloviusExists = await verifyResourceExists(gloviusFallbackUrl);
			if (cancelled) return;
			if (gloviusExists) {
				setKind("glovius");
				setModelUrl(gloviusFallbackUrl);
			} else {
				setError("This 3D model is no longer available. Please open it again from the machine page.");
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [stateUrl, inhouseFallbackUrl, gloviusFallbackUrl]);

	const markReady = useCallback(() => {
		if (fallbackTimerRef.current) {
			clearTimeout(fallbackTimerRef.current);
			fallbackTimerRef.current = null;
		}
		setPercentage(100);
		setReady(true);
	}, []);

	// Glovius posts { source: "glovius", stage } from its FileLoadedEvent once the CAD payload
	// is parsed and on screen — see the notifyHost helper in model.controller.js's template.
	// In-house pages also post { stage: "progress", percent } from their own loader
	// (ISTP2HTML_HOST_READY_SCRIPT), the only real number this curtain can show for them.
	useEffect(() => {
		const onMessage = (event) => {
			if (event?.data?.source !== "glovius") return;
			if (event.data.stage === "file-loaded") markReady();
			if (event.data.stage === "progress") {
				const raw = Math.min(100, Math.max(0, Number(event.data.percent) || 0));
				// The download already owns 0..DOWNLOAD_SHARE, so the page's own loader
				// fills the parse share above it. 100 belongs to the file-loaded ping alone,
				// and Math.max keeps a late ping from walking the bar backwards.
				const pct = DOWNLOAD_SHARE + (raw / 100) * (PARSE_CEILING - DOWNLOAD_SHARE);
				setPercentage((prev) => (typeof prev === "number" ? Math.max(prev, pct) : pct));
			}
			if (event.data.stage === "drawer-open") setDrawerOpen(true);
			if (event.data.stage === "drawer-closed") setDrawerOpen(false);
			if (event.data.stage === "contact-ccr-request") {
				if (showContactCcr) setCcrOpen(true);
				return;
			}
			if (event.data.stage === "cart-submitted") {
				const destination = event.data.destination === "/requests" ? "/requests" : "/cart";
				const destinationLabel = destination === "/cart" ? "Cart" : "Request List";
				const totalCartItems = Number(event.data.cartCount) || 0;
				const totalRequestItems = Number(event.data.requestCount) || 0;
				const redirectSeconds = 5;
				let remainingSeconds = redirectSeconds;
				let countdownInterval = null;

				const redirectFromSwal = () => {
					if (destination === "/cart") {
						const params = new URLSearchParams({
							mvSource: "htmlviewer",
							mvCartCount: String(totalCartItems),
							mvRequestCount: String(totalRequestItems),
						});
						navigate(`${destination}?${params.toString()}`);
					} else {
						navigate(destination);
					}
				};

				Swal.fire({
					icon: "success",
					title: "<div style='text-align:center;width:100%;'>Items Submitted</div>",
					html: `
						<div style='text-align:center;width:100%;'>
							<b>Added to Cart:</b> ${totalCartItems}<br/>
							<b>Added to Request List:</b> ${totalRequestItems}<br/><br/>
							Redirecting to ${destinationLabel} in <b><span id="swal-cart-countdown">${remainingSeconds}</span>s</b>.
						</div>
					`,
					// The countdown still redirects on its own, but waiting five
					// seconds to reach the cart you just filled reads as nothing
					// having happened. The button goes there now; both paths run
					// redirectFromSwal, so they cannot diverge.
					showConfirmButton: true,
					confirmButtonText: `Go to ${destinationLabel}`,
					allowOutsideClick: false,
					allowEscapeKey: false,
					didOpen: () => {
						const countdownEl = document.getElementById("swal-cart-countdown");
						countdownInterval = setInterval(() => {
							remainingSeconds -= 1;
							if (remainingSeconds <= 0) {
								clearInterval(countdownInterval);
								countdownInterval = null;
								Swal.close();
								return;
							}
							if (countdownEl) {
								countdownEl.textContent = remainingSeconds;
							}
						}, 1000);
					},
					willClose: () => {
						if (countdownInterval) {
							clearInterval(countdownInterval);
							countdownInterval = null;
						}
					},
				}).then(() => {
					redirectFromSwal();
				});
			}
		};
		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, [markReady, navigate, showContactCcr]);

	// Download the document HERE rather than letting the iframe do it, so the progress the user
	// sees is the actual byte count instead of a timer pretending to be one. The bytes are
	// handed to the iframe as a blob: URL.
	//
	// Re-runs on framedUrl, which is what a hard refresh changes — so a mid-download refresh
	// aborts the in-flight reader (rather than leaving it running, still writing state into a
	// component that has moved on) and revokes the blob URL it was going to produce.
	useEffect(() => {
		if (!framedUrl) return undefined;

		const controller = new AbortController();
		// Captured by the cleanup below. Assigned only once the blob actually exists, so the
		// cleanup never revokes a URL that was never created.
		let objectUrl = "";

		setBlobUrl("");
		setReceivedBytes(0);
		setTrustedTotalBytes(0);

		(async () => {
			try {
				const response = await fetchModelAsset(framedUrl, { signal: controller.signal });
				if (response.status === 401) {
					// fetchModelAsset has already raised session-expired: the auth context
					// drops the user and ProtectedRoute sends them to /login. Name the reason
					// in case that redirect is a beat behind the curtain.
					setError("Your session has expired. Please log in again to view this 3D model.");
					return;
				}
				// A failed download is a dead end. The page sits behind the login wall, so the
				// frame cannot be pointed at the URL instead — an iframe sends no bearer header
				// and would render the 401 body — and doing so would undo the wall anyway.
				if (!response.ok || !response.body) throw new Error(`Model request failed (${response.status})`);

				// THE TOTAL IS THE PART THAT HAS TO BE RIGHT.
				//
				// Content-Length counts the bytes ON THE WIRE; the reader below yields bytes
				// AFTER the browser has transparently decoded any Content-Encoding. These
				// documents are 3.6-16 MB of base64 and compress by roughly 4x, so dividing
				// decoded bytes by a compressed Content-Length races past 100% and lands
				// somewhere near 400% — worse than no number at all.
				//
				// So a total is only trusted when a length is declared AND no encoding is.
				// The server does not currently run compression middleware, but a reverse
				// proxy or CDN in front of it can gzip without anyone here knowing.
				let declaredTotal = 0;
				const contentLength = response.headers.get("content-length");
				if (contentLength && !response.headers.get("content-encoding")) {
					declaredTotal = Number(contentLength) || 0;
				}

				const reader = response.body.getReader();
				const chunks = [];
				let bytes = 0;
				let paintedAt = 0;

				for (;;) {
					const { done, value } = await reader.read();
					if (done) break;
					// Chunks are kept as separate Uint8Arrays and only assembled by the Blob
					// constructor. Joining them into one string or one buffer first would hold
					// the whole 16 MB twice at the moment of the copy.
					chunks.push(value);
					bytes += value.byteLength;

					// Second line of defence on the total, and the one that actually holds
					// cross-origin: Content-Encoding is NOT a CORS-safelisted response header,
					// so on a gzipped response from another origin the check above can see a
					// Content-Length while the encoding header is invisible to it. Overshoot
					// is the proof the header lied — drop the total permanently and finish the
					// download as a byte counter.
					if (declaredTotal && bytes > declaredTotal) declaredTotal = 0;

					if (bytes - paintedAt >= PROGRESS_PAINT_BYTES) {
						paintedAt = bytes;
						setReceivedBytes(bytes);
						setTrustedTotalBytes(declaredTotal);
					}
				}

				setReceivedBytes(bytes);
				setTrustedTotalBytes(declaredTotal);
				objectUrl = URL.createObjectURL(new Blob(chunks, { type: DOCUMENT_MIME }));
				setBlobUrl(objectUrl);
				// Download is provably over; the remaining wait is Glovius's and unmeasurable.
				setStep(renderingStep);
			} catch (err) {
				// An abort is this effect's own cleanup, not a failure — the component is
				// unmounting or reloading and there is nothing left to report.
				if (controller.signal.aborted) return;
				console.warn("HtmlModelViewer: model download failed.", err);
				setError("This 3D model is no longer available. Please open it again from the machine page.");
			}
		})();

		return () => {
			controller.abort();
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [framedUrl, renderingStep]);

	// PHASE 1 — download. Real bytes mapped onto 0..DOWNLOAD_SHARE, and only ever shown as a
	// percentage when the total is trustworthy. With no trustworthy total the value stays null,
	// which renders as the bare animation plus the live MB counter instead of a number this
	// code cannot stand behind.
	//
	// Monotonic via Math.max so a repaint can never walk the bar backwards.
	useEffect(() => {
		if (ready || error) return;
		if (blobUrl) return;                      // phase 2 owns the bar from here
		if (!trustedTotalBytes) { setPercentage(null); return; }

		const pct = Math.min(DOWNLOAD_SHARE, (receivedBytes / trustedTotalBytes) * DOWNLOAD_SHARE);
		setPercentage((prev) => Math.max(typeof prev === "number" ? prev : 0, pct));
	}, [ready, error, blobUrl, receivedBytes, trustedTotalBytes]);

	// PHASE 2 — the Glovius parse. Between the last byte and the `file-loaded` ping there is
	// NOTHING to subscribe to: Glovius exposes only ControlLoadedEvent/FileLoadedEvent, no
	// progress callback. So the bar eases asymptotically toward PARSE_CEILING on a clock scaled
	// by the size of the document we just finished streaming — which, unlike the GLB viewer's
	// HEAD-derived estimate, is a byte count we know exactly.
	//
	// This runs on the bytes-only path too: the creep needs elapsed time and file size, neither
	// of which depends on a trusted Content-Length. `prev` is null there, and the `from`
	// expression starts such a run at DOWNLOAD_SHARE rather than coercing null to 0.
	//
	// WHAT THE ARITHMETIC ACTUALLY DOES (measured, not asserted — scratchpad check over 20k
	// ticks per size): at 1800 ms/MB a 3.6 MB document expects a 6.5s parse, sits at 95.7 when
	// that elapses, and first ROUNDS to 99 at 19.2s; a 16 MB document expects 28.8s, is also at
	// ~95.7 then, and first rounds to 99 at 83.6s — after READY_FALLBACK_MS has already fired.
	// It does eventually float-saturate at exactly 99 (205s / 858s respectively), which is
	// unavoidable without inventing a completion time. It can never display 100: only markReady
	// sets that.
	//
	// Guaranteed by the arithmetic, not by hope: `from` is floored at DOWNLOAD_SHARE, the step
	// is a fraction of the REMAINING gap, and a `from` already at the ceiling returns unchanged.
	// That last guard is not decoration — without it a tick still queued when markReady set 100
	// computes 100 + (99-100)*ease and drags the bar back down to 99.5.
	useEffect(() => {
		if (ready || error || !blobUrl) return undefined;

		const expectedParseMs = Math.max(PARSE_TICK_MS, (receivedBytes / 1048576) * PARSE_MS_PER_MB);
		// Exponential time constant: after expectedParseMs the bar sits ~63% of the way from
		// DOWNLOAD_SHARE to PARSE_CEILING, then closes the remaining gap ever more slowly.
		const easePerTick = PARSE_TICK_MS / (expectedParseMs + PARSE_TICK_MS);

		const timer = setInterval(() => {
			setPercentage((prev) => {
				const from = typeof prev === "number" ? Math.max(prev, DOWNLOAD_SHARE) : DOWNLOAD_SHARE;
				if (from >= PARSE_CEILING) return from;
				return from + (PARSE_CEILING - from) * easePerTick;
			});
		}, PARSE_TICK_MS);
		return () => clearInterval(timer);
	}, [ready, error, blobUrl, receivedBytes]);

	// Step the message on a clock. It stops one short of renderingStep — that message means
	// "Glovius is parsing", and the download effect sets it when that is actually true, so the
	// timer must not reach it early. `prev >= max ? prev : prev + 1` rather than Math.min
	// because the download effect can push step ABOVE this ceiling, and clamping would then
	// walk the message backwards. The bar itself is never invented here: the download effect
	// and the page's own "progress" pings are its only sources.
	useEffect(() => {
		if (ready || error) return undefined;
		const maxStep = renderingStep - 1;
		const stepTimer = setInterval(
			() => setStep((prev) => (prev >= maxStep ? prev : prev + 1)),
			STEP_INTERVAL_MS,
		);
		return () => clearInterval(stepTimer);
	}, [ready, error, renderingStep]);

	useEffect(() => () => {
		if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
	}, []);

	/**
	 * The frame has a parsed document with its scripts running. The bytes were already in
	 * memory, so this fires almost immediately after the blob URL is set.
	 *
	 * It is NOT "the model is visible" — Glovius starts parsing the CAD payload
	 * now — so it still only advances the message and arms the safety timeout. It is also the
	 * earliest point at which Glovius exists to post its readiness ping, which is why the
	 * timeout is armed here and not when the download completes.
	 *
	 * @returns {void}
	 */
	const handleIframeLoad = useCallback(() => {
		sendContactCcrAvailability();
		sendHtmlOrientation();
		// Both kinds: the document is parsed and its scripts are running, the model is
		// not on screen yet. Advance the message and arm the valve; the curtain drops on
		// the page's file-loaded ping (Glovius FileLoadedEvent, or the in-house page's
		// #loader finishing), so its own loader is never seen alongside this one.
		setStep(renderingStep);
		if (!fallbackTimerRef.current) {
			fallbackTimerRef.current = setTimeout(markReady, READY_FALLBACK_MS);
		}
	}, [markReady, renderingStep, sendContactCcrAvailability, sendHtmlOrientation]);

	/**
	 * Reloads the model. The GLB viewer's equivalent reloads the whole PAGE with a
	 * `_hard_refresh` timestamp because its model lives in the page; here the model lives in
	 * the iframe, so busting the frame's URL is the same act without discarding the router
	 * state that carries `modelUrl`. The token also keys the iframe, forcing a real remount so
	 * onLoad fires again and the loading curtain reappears.
	 *
	 * The blob URL is dropped HERE and not left to the download effect's own reset, even though
	 * that effect clears it a moment later. Changing reloadToken changes the iframe's key, so
	 * React commits a remount before any effect runs — and with the previous blob still in
	 * state, that remount would put the OLD document back in the frame just long enough to fire
	 * onLoad, arming the 45s readiness timeout against a download that had not started yet.
	 * Clearing it in the same batch means the frame simply unmounts instead.
	 *
	 * @returns {void}
	 */
	const handleHardRefresh = useCallback(() => {
		if (fallbackTimerRef.current) {
			clearTimeout(fallbackTimerRef.current);
			fallbackTimerRef.current = null;
		}
		setReady(false);
		setStep(0);
		setPercentage(null);
		setError("");
		setDrawerOpen(false);
		setBlobUrl("");
		setReloadToken(Date.now());
	}, []);

	/**
	 * Leaves the viewer, warning first — Glovius keeps its own model cart (the badged FAB),
	 * whose contents never reach the application cart. Same confirmation the GLB viewer shows
	 * for the same reason.
	 *
	 * @returns {void}
	 */
	const handleCloseModel = useCallback(() => {
		Swal.fire({
			title: t("UI_TEXT.ARE_YOU_SURE_YOU_WANT_TO_CLOSE"),
			text: t("UI_TEXT.ANY_ITEMS_ADDED_IN_THE_MODEL_CART_WILL_NOT_BE_ADDED_TO_THE_APPLICATION_CART"),
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: t("BUTTON.YES_CLOSE"),
			cancelButtonText: t("BUTTON.CANCEL"),
		}).then((result) => {
			if (!result.isConfirmed) return;
			if (typeof fromPath === "string" && fromPath.trim()) navigate(fromPath);
			else navigate(-1);
		});
	}, [fromPath, navigate, t]);

	const showOverlay = !ready || !!error;

	// The frame only ever loads the downloaded bytes (blobUrl), never the URL itself — the
	// page is behind the login wall and an iframe cannot send the bearer header. It is
	// rendered conditionally on blobUrl rather than on modelUrl, because an iframe with
	// src="" loads about:blank and fires onLoad — which would arm the readiness timeout and
	// jump the message to "Rendering" while the download was still running.
	useEffect(() => {
		if (!blobUrl || error) return;
		sendContactCcrAvailability();
		sendHtmlOrientation();
	}, [error, blobUrl, sendContactCcrAvailability, sendHtmlOrientation]);

	// THE BAR, PHASE BY PHASE (the value itself is owned by the two phase effects above):
	//
	//   downloading + trusted total    -> 0-90, real bytes over a real total
	//   downloading + no trusted total -> no number, plus a live "... 8.2 MB downloaded"
	//   parsing (blob handed over)     -> 90-99 creep, on BOTH of the above paths; an in-house
	//                                     page's real "progress" pings are laid over that range
	//   ready, or the fallback valve   -> 100
	//
	// The MB counter is the only readout still derived at render time, because it is the one
	// with no phase-2 equivalent: once the download is over there are no further bytes to
	// report and the number takes over.
	const downloading = !blobUrl;
	const loadingText =
		downloading && !trustedTotalBytes && receivedBytes
			? `${loadingMessages[step]} ${formatMb(receivedBytes)} downloaded`
			: loadingMessages[step];

	// Shell and overlay are copied from ModelViewer's own markup (the GLB viewer) so the two
	// 3D formats are visually indistinguishable to the user — same 100dvh/100vw stage, same
	// zIndex-999 theme-aware loading curtain.
	return (
		<Box
			sx={{
				position: "relative",
				height: "100dvh",
				width: "100vw",
				overflow: "hidden",
			}}
		>
			{blobUrl && !error && (
				<iframe
					ref={iframeRef}
					key={reloadToken}
					src={blobUrl}
					title={machineName || fileName || "3D model"}
					onLoad={handleIframeLoad}
					style={{ width: "100%", height: "100%", border: "none", display: "block" }}
					// The document is server content we generate ourselves, so no sandbox:
					// Glovius needs blob: URLs, WebGL and full script access, and a sandbox
					// attribute without allow-same-origin breaks its own asset loading.
					allow="fullscreen; xr-spatial-tracking"
				/>
			)}

			{drawerOpen && (
				<Box
					sx={{
						position: "absolute",
						top: 0,
						right: 0,
						bottom: 0,
						left: "55vw",
						zIndex: 1000,
						backgroundColor: "transparent",
						backdropFilter: "none",
						pointerEvents: "none",
						"@media (max-width: 768px)": {
							left: "80vw",
						},
						"@media (max-width: 480px)": {
							left: "88vw",
						},
						"@media (min-width: 1025px)": {
							display: "none",
						},
					}}
				/>
			)}

			{showOverlay && (
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						zIndex: 999,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						background: (theme) =>
							theme.palette.mode === "dark"
								? "rgba(0,0,0,0.8)"
								: "rgba(255,255,255,1)",
					}}
				>
					{error ? (
						<Box sx={{ textAlign: "center", px: 3 }}>
							<Typography color="error" variant="h6" mb={1}>
								{error}
							</Typography>
							<Typography variant="body2">
								Please try refreshing the page or going back.
							</Typography>
						</Box>
					) : (
						<LoadingBoxes
							text={loadingText}
							subText="Note: The 3D model may take up to a minute to fully load."
							percentage={percentage}
						/>
					)}
				</Box>
			)}
			{showContactCcr && <ContactCCRForm open={ccrOpen} setOpen={setCcrOpen} prefill={ccrPrefill} />}

			{/* NO PageTitle here. The Glovius document renders its own "<number> | <name>"
			    heading at runtime (assets/js/ithena.js, from the machinename data-value), so
			    adding the app's centred PageTitle on top produced the same text twice. Its
			    styling differs from the GLB viewer's PageTitle, but removing it would mean
			    editing ithena.js — shared third-party-ish viewer code — to gain nothing the
			    user can't already read. */}

			{/* Hard-refresh + close, matching ModelViewer's top-right cluster values exactly.
			    Sits ABOVE the loading curtain (999) on purpose: the document can take ~30s to
			    parse, and being unable to leave or retry during that is the worst moment to
			    have no controls. */}
			<Box
				sx={{
					position: "absolute",
					top: { xs: 12, sm: 16 },
					right: { xs: 12, sm: 24 },
					zIndex: 1000,
					display: "flex",
					"@media (max-width: 1024px)": {
						display: drawerOpen ? "none" : "flex",
					},
					alignItems: "center",
					gap: 0.5,
					pointerEvents: "auto",
					// No container surface at any width — no background, border, radius,
					// padding or shadow. This matches ModelViewer.js's top-right cluster,
					// which has none either. The white pill that used to sit here below
					// 1025px was also masking Glovius's own #closemodel-btn underneath, so
					// dropping it is what surfaced the duplicate close icon now hidden by
					// the glb-parity block in model.controller.js.
				}}
			>
				<Tooltip title="Refresh Page">
					<IconButton onClick={handleHardRefresh} sx={controlButtonSx}>
						<Autorenew />
					</IconButton>
				</Tooltip>
				<Tooltip title={t("UI_TEXT.TOOLTIP_CLOSE_MODEL_VIEW")}>
					<IconButton onClick={handleCloseModel} sx={controlButtonSx}>
						<Close />
					</IconButton>
				</Tooltip>
			</Box>
		</Box>
	);
};

// Keep the Master HTML host and its native converter controller. AMF-specific
// catalog, ordering, and theme data are injected by process-modelfile.
const HtmlModelViewer = () => <LegacyHtmlModelViewer />;

export default HtmlModelViewer;
